import { ReadonlyVec2, ReadonlyVec3 } from 'gl-matrix';
import { resampleUint8Grid } from '~/domain/services/resampleGrid';
import { Grid } from '~/engine/data/common/Grid';
import {
    RegionOfInterest,
    TextureWrite,
} from '~/engine/data/structure/RegionOfInterest';
import { LayerBit } from '~/engine/view/layers/LayerBit';
import { TextureView } from '~/engine/view/TextureView';
import { PlaneView } from '~/engine/view/views/PlaneView';
import {
    EventHandlerResult,
    EventHandlerResultType,
} from '../../EventHandlerResult';
import { ModifierKeys } from '../../ModifierKeys';
import { ToolAction } from '../../ToolAction';
import {
    buildAxisAlignedDestGrid,
    buildPatientSrcFromDstRotation,
    buildRotationAroundPivot,
    flattenSlices,
    unflattenToSlices,
} from './transformUtils';

/**
 * Maximum allowed rotation angle in radians (~45 degrees).
 * Large rotations dramatically change the structure's
 * relationship to anatomy and are rarely clinically intended.
 */
const MAX_ROTATION_RAD = Math.PI / 4;

/** Minimum angle change to trigger a grid update (radians). */
const ANGLE_THRESHOLD = 0.001;

/**
 * Action that handles interactive rotation of a structure's SDF
 * texture around the viewing plane's normal, pivoting on the
 * structure's geometric centre.
 *
 * Rotation input uses angular displacement around the structure
 * centre: the user grabs the circle outline of the gizmo and
 * drags along the arc.
 *
 * During drag only the grid transform is updated (the grid is
 * rotated in world space while pixel data stays unchanged).
 * On release the pixels are resampled into a new grid aligned
 * with the image grid's orientation and snapped to its voxel
 * lattice.
 */
export class StructureRotateAction extends ToolAction {
    private readonly _roi: RegionOfInterest;

    private _startView?: PlaneView;
    private _centreCanvas?: ReadonlyVec2;
    private _structureCentre?: ReadonlyVec3;
    private _rotationAxis?: ReadonlyVec3;
    private _startAngle?: number;
    private _originalGrid?: Grid;
    private _originalFlatPixels?: Uint8Array;
    private _textureWrite?: TextureWrite;
    private _lastAppliedAngle = 0;

    constructor(roi: RegionOfInterest) {
        super();
        this._roi = roi;
    }

    public onMousePress(
        _button: number,
        _modifierKeys: ModifierKeys,
        coordinates: ReadonlyVec2,
        view: TextureView
    ): EventHandlerResult {
        if (!(view instanceof PlaneView)) {
            return new EventHandlerResult(
                EventHandlerResultType.EVENT_NOT_HANDLED
            );
        }

        const texture = this._roi.texture;
        if (!texture || texture.isEmpty) {
            return new EventHandlerResult(
                EventHandlerResultType.EVENT_NOT_HANDLED
            );
        }

        this._startView = view;
        this._structureCentre = texture.textureGrid.center;
        this._rotationAxis = view.viewDirection;

        this._centreCanvas = view.canvasCoordsFromPatientCoords(
            this._structureCentre
        );

        const canvasCoords = view.clientToCanvas(coordinates);
        this._startAngle = Math.atan2(
            canvasCoords[1] - this._centreCanvas[1],
            canvasCoords[0] - this._centreCanvas[0]
        );

        this._originalGrid = texture.textureGrid.clone();
        this._originalFlatPixels = flattenSlices(
            texture.pixels,
            this._originalGrid
        );
        this._lastAppliedAngle = 0;

        return new EventHandlerResult(
            EventHandlerResultType.EVENT_HANDLED,
            true
        );
    }

    public onMouseMove(
        coordinates: ReadonlyVec2,
        _buttons: number,
        _view: TextureView
    ): EventHandlerResult {
        if (
            !this._startView ||
            !this._centreCanvas ||
            !this._structureCentre ||
            this._startAngle === undefined ||
            !this._rotationAxis ||
            !this._originalGrid ||
            !this._originalFlatPixels
        ) {
            return new EventHandlerResult(
                EventHandlerResultType.EVENT_NOT_HANDLED
            );
        }

        const canvasCoords = this._startView.clientToCanvas(coordinates);
        const currentAngle = Math.atan2(
            canvasCoords[1] - this._centreCanvas[1],
            canvasCoords[0] - this._centreCanvas[0]
        );

        let deltaAngle = currentAngle - this._startAngle;

        // Normalise to [-PI, PI] to handle wrap-around
        if (deltaAngle > Math.PI) {
            deltaAngle -= 2 * Math.PI;
        } else if (deltaAngle < -Math.PI) {
            deltaAngle += 2 * Math.PI;
        }

        // Clamp to maximum rotation
        deltaAngle = Math.max(
            -MAX_ROTATION_RAD,
            Math.min(MAX_ROTATION_RAD, deltaAngle)
        );

        // Canvas Y is inverted relative to the mathematical
        // convention (Y points down), so negate the angle to make
        // the visual rotation match the drag direction.
        const angle = -deltaAngle;

        if (Math.abs(angle - this._lastAppliedAngle) < ANGLE_THRESHOLD) {
            return new EventHandlerResult(
                EventHandlerResultType.EVENT_HANDLED,
                true
            );
        }

        if (!this._textureWrite) {
            this._textureWrite = this._roi.startModify('rotate');
        }

        const texture = this._roi.texture;
        if (!texture) {
            return new EventHandlerResult(
                EventHandlerResultType.EVENT_NOT_HANDLED
            );
        }

        // Build forward rotation and apply to the original grid
        const rotMat = buildRotationAroundPivot(
            this._structureCentre,
            this._rotationAxis,
            angle
        );
        const rotatedGrid = this._originalGrid.transform(rotMat);
        texture.resetGrid(rotatedGrid);
        this._lastAppliedAngle = angle;

        return new EventHandlerResult(
            EventHandlerResultType.EVENT_HANDLED,
            true,
            { layers: LayerBit.CONTOUR_LAYER_BIT }
        );
    }

    public onMouseRelease(
        _button: number,
        _coordinates: ReadonlyVec2
    ): EventHandlerResult {
        if (this._textureWrite) {
            try {
                const texture = this._roi.texture;
                if (
                    texture &&
                    this._originalGrid &&
                    this._originalFlatPixels &&
                    this._structureCentre &&
                    this._rotationAxis
                ) {
                    const angle = this._lastAppliedAngle;

                    // Build the forward rotation matrix (same as used
                    // during drag) so we can compute the destination
                    // grid from the rotated bounding box
                    const forwardRotMat = buildRotationAroundPivot(
                        this._structureCentre,
                        this._rotationAxis,
                        angle
                    );

                    // Destination grid: aligned with image grid,
                    // encompassing the rotated structure
                    const dstGrid = buildAxisAlignedDestGrid(
                        this._originalGrid,
                        forwardRotMat,
                        texture.grid
                    );

                    // Inverse rotation for the resampler (dst → src)
                    const patientSrcFromDst = buildPatientSrcFromDstRotation(
                        this._structureCentre,
                        this._rotationAxis,
                        angle
                    );

                    const result = resampleUint8Grid(
                        this._originalFlatPixels,
                        this._originalGrid,
                        dstGrid,
                        { patientSrcFromDst, backgroundValue: 0 }
                    );
                    const dstPixels = unflattenToSlices(
                        result.pixels,
                        dstGrid.sizeX,
                        dstGrid.sizeY,
                        dstGrid.sizeZ
                    );
                    texture.resetSdfAndGrid(dstPixels, dstGrid);
                }
            } finally {
                this._textureWrite.finish();
            }
        }

        this._startView = undefined;
        this._centreCanvas = undefined;
        this._structureCentre = undefined;
        this._rotationAxis = undefined;
        this._startAngle = undefined;
        this._originalGrid = undefined;
        this._originalFlatPixels = undefined;
        this._textureWrite = undefined;

        return new EventHandlerResult(
            EventHandlerResultType.EVENT_HANDLED,
            false,
            { layers: LayerBit.CONTOUR_LAYER_BIT }
        );
    }
}
